import { Component, type ReactNode, lazy, Suspense, useCallback, useEffect, useMemo } from "react";
import "ketcher-react/dist/index.css";

type Ketcher = import("ketcher-core").Ketcher;

interface KetcherEditorProps {
  ketFile?: string;
  onUpdate?: (update: any, ketcher: Ketcher) => void;
}

const KETCHER_STATIC_RESOURCES_URL = "/";

const toError = (error: unknown): Error => {
  if (error instanceof Error) return error;
  return new Error(typeof error === "string" ? error : JSON.stringify(error));
};

const logKetcherError = (context: string, error: unknown, extra?: Record<string, unknown>) => {
  const normalizedError = toError(error);
  console.error(`[Ketcher] ${context}`, {
    message: normalizedError.message,
    stack: normalizedError.stack,
    ...extra,
  });
};

class KetcherErrorBoundary extends Component<
  { children: ReactNode },
  { hasError: boolean; error?: Error }
> {
  state = { hasError: false, error: undefined as Error | undefined };

  static getDerivedStateFromError(error: Error) {
    logKetcherError("ErrorBoundary caught render error", error);
    return { hasError: true, error };
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex items-center justify-center h-full p-8 text-center">
          <div>
            <p className="text-sm font-medium text-destructive mb-1">Chemistry editor failed to load</p>
            <p className="text-xs text-muted-foreground">{this.state.error?.message}</p>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

if (typeof window !== "undefined" && !(window as any).ketcher) {
  (window as any).ketcher = { logging: {} };
}

const KetcherEditorLazy = lazy(async () => {
  const [{ Editor: KEditor }, { StandaloneStructServiceProvider }] = await Promise.all([
    import("ketcher-react"),
    import("ketcher-standalone"),
  ]);

  const KetcherEditorInner = ({ ketFile = "", onUpdate }: KetcherEditorProps) => {
    const structServiceProvider = useMemo(() => new StandaloneStructServiceProvider(), []);

    useEffect(() => {
      const originalFetch = window.fetch.bind(window);

      window.fetch = ((input: RequestInfo | URL, init?: RequestInit) => {
        const url =
          typeof input === "string"
            ? input
            : input instanceof URL
              ? input.toString()
              : input.url;

        if (url.includes("/templates/") && url.endsWith(".svg")) {
          return Promise.resolve(
            new Response("", {
              status: 200,
              headers: { "Content-Type": "image/svg+xml" },
            })
          );
        }

        return originalFetch(input as RequestInfo, init);
      }) as typeof window.fetch;

      return () => {
        window.fetch = originalFetch;
      };
    }, []);

    useEffect(() => {
      const onWindowError = (event: ErrorEvent) => {
        const text = `${event.message} ${event.filename}`.toLowerCase();
        if (text.includes("ketcher") || text.includes("indigo")) {
          logKetcherError("window.error", event.error || event.message, {
            filename: event.filename,
            lineno: event.lineno,
            colno: event.colno,
          });
        }
      };

      const onUnhandledRejection = (event: PromiseRejectionEvent) => {
        const reasonText = String(event.reason ?? "").toLowerCase();
        if (reasonText.includes("ketcher") || reasonText.includes("indigo")) {
          logKetcherError("window.unhandledrejection", event.reason);
        }
      };

      window.addEventListener("error", onWindowError);
      window.addEventListener("unhandledrejection", onUnhandledRejection);

      return () => {
        window.removeEventListener("error", onWindowError);
        window.removeEventListener("unhandledrejection", onUnhandledRejection);
      };
    }, []);

    const onInit = useCallback(
      (ketcher: Ketcher) => {
        console.info("[Ketcher] onInit start", {
          hasKetFile: Boolean(ketFile),
          ketFileLength: ketFile.length,
          staticResourcesUrl: KETCHER_STATIC_RESOURCES_URL,
          pathname: window.location.pathname,
        });

        try {
          ketcher.editor.subscribe("change", (data: any) => {
            try {
              onUpdate?.(data, ketcher);
            } catch (error) {
              logKetcherError("onUpdate callback failed", error);
            }
          });

          if (ketFile.trim().length > 0) {
            Promise.resolve(ketcher.setMolecule(ketFile)).catch((error: unknown) => {
              logKetcherError("setMolecule failed", error, {
                ketFileLength: ketFile.length,
              });
            });
          }
        } catch (error) {
          logKetcherError("onInit failed", error);
          throw error;
        }
      },
      [ketFile, onUpdate]
    );

    return (
      <KEditor
        staticResourcesUrl={KETCHER_STATIC_RESOURCES_URL}
        structServiceProvider={structServiceProvider}
        errorHandler={(error: unknown) => logKetcherError("editor errorHandler", error)}
        disableMacromoleculesEditor={true}
        onInit={onInit}
      />
    );
  };

  return { default: KetcherEditorInner };
});

export const KetcherEditor = (props: KetcherEditorProps) => (
  <KetcherErrorBoundary>
    <Suspense
      fallback={
        <div className="flex items-center justify-center h-full p-8 text-center">
          <p className="text-sm text-muted-foreground">Loading chemistry editor…</p>
        </div>
      }
    >
      <KetcherEditorLazy {...props} />
    </Suspense>
  </KetcherErrorBoundary>
);
